import * as React from 'react'
import { useStableId } from '@/ui/form/hooks/useStableId'
import { FieldHelper, FieldErrorMessage, Input } from '@/ui/form/primitives'

type Props = React.ComponentProps<typeof Input> & {
  label?: string
  helper?: React.ReactNode
  labelRight?: React.ReactNode
  error?: string | null
  fullWidth?: boolean
  innerLabel?: string
  inputSize?: 'small' | 'big'
  allowDecimal?: boolean
  decimalPlaces?: number
}

const NumberFieldInnerTable = ({
  label,
  labelRight,
  helper,
  error,
  id,
  fullWidth,
  value,
  innerLabel,
  inputSize,
  allowDecimal = true,
  decimalPlaces,
  onChange,
  ...props
}: Props) => {
  const autoId = useStableId()
  const isBig = inputSize === 'big'
  const inputId = id ?? `autoid-${autoId}`
  const helperId = helper ? `${inputId}-helper` : undefined
  const errorId = error ? `${inputId}-error` : undefined
  const describedBy = [helperId, errorId].filter(Boolean).join(' ') || undefined

  const formatDisplay = React.useCallback(
    (raw: string | number | readonly string[] | undefined) => {
      const str = raw?.toString() ?? ''
      if (decimalPlaces === undefined || str === '') return str
      const num = Number(str.replace(/,/g, '.'))
      if (Number.isNaN(num)) return str
      return num.toFixed(decimalPlaces)
    },
    [decimalPlaces],
  )

  const [localValue, setLocalValue] = React.useState<string>(
    formatDisplay(value),
  )
  const isFocused = React.useRef(false)

  React.useEffect(() => {
    if (!isFocused.current) {
      setLocalValue(formatDisplay(value))
    }
  }, [value, formatDisplay])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    let sanitized: string
    if (allowDecimal) {
      sanitized = val.replace(/,/g, '.').replace(/[^0-9.]/g, '')
      const parts = sanitized.split('.')
      if (parts.length > 2) {
        sanitized = parts[0] + '.' + parts.slice(1).join('')
      }
      // Max 2 decimal places
      if (parts.length === 2 && parts[1].length > 2) {
        sanitized = parts[0] + '.' + parts[1].slice(0, 2)
      }
    } else {
      sanitized = val.replace(/[^0-9]/g, '')
    }
    setLocalValue(sanitized)
    if (onChange) {
      const newEvent = {
        ...e,
        target: {
          ...e.target,
          value: sanitized,
        },
      } as React.ChangeEvent<HTMLInputElement>
      onChange(newEvent)
    }
  }

  const handleFocus = () => {
    isFocused.current = true
  }

  const handleBlur = () => {
    isFocused.current = false
    setLocalValue(formatDisplay(value))
  }
  return (
    <div
      data-control="text-field"
      className={`tw-flex tw-flex-col tw-gap-1 ${fullWidth ? 'tw-w-full' : ''}`}
    >
      <div>
        {label && (
          <div className="tw-mb-1 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-700">
            <label htmlFor={inputId} className="tw-m-0">
              {label}
            </label>
          </div>
        )}

        <div
          className={`tw-focus-within:tw-ring-2 tw-focus-within:tw-ring-blue-500 tw-focus-within:tw-border-blue-500 tw-relative tw-flex tw-items-stretch tw-rounded-md tw-border tw-border-gray-300 tw-bg-white ${fullWidth ? 'tw-w-full' : 'tw-max-w-[150px]'}`}
        >
          {innerLabel && (
            <label
              htmlFor={inputId}
              className={`tw-m-0 tw-flex tw-select-none ${
                isBig
                  ? 'tw-items-center tw-text-sm'
                  : 'tw-items-end tw-pb-1 tw-text-xs'
              } tw-pl-2 tw-font-medium tw-text-content-base`}
            >
              {innerLabel}
            </label>
          )}
          <Input
            id={inputId}
            aria-describedby={describedBy}
            aria-invalid={!!error || undefined}
            value={localValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={`tw-block tw-w-full tw-rounded-md ${isBig ? 'tw-px-3 tw-py-2' : 'tw-px-2 tw-py-1'} tw-text-sm tw-leading-tight tw-placeholder-gray-400 tw-shadow-sm disabled:tw-cursor-not-allowed disabled:tw-bg-gray-100 disabled:tw-text-gray-400 data-[invalid=true]:tw-border-red-500 data-[invalid=true]:focus:tw-ring-red-500`}
            {...props}
          />
        </div>
      </div>

      {helper && (
        <FieldHelper
          id={helperId}
          className="tw-mt-0.5 tw-text-xs tw-text-gray-500"
        >
          {helper}
        </FieldHelper>
      )}

      {error && (
        <FieldErrorMessage
          id={errorId}
          className="tw-mt-0.5 tw-text-xs tw-font-medium tw-text-red-600"
        >
          {error}
        </FieldErrorMessage>
      )}
    </div>
  )
}

export default NumberFieldInnerTable
